// Pure logic for the Copy Job builder: which job types exist, what each one's
// form looks like, how a saved job maps back into the form, and how the form maps
// onto the exact API request the backend already accepts.
//
// Kept free of React and of the "@/" alias so it can be unit-tested directly.
// Nothing here invents backend behaviour: every field and every endpoint below
// corresponds to a request model in backend/server.py (JobReq, JobUpdateReq,
// ArchiveJobReq, ReplayJobReq).

// Descriptions follow the backend's actual semantics, not the marketing version:
//   * archive  -> ArchiveJobReq / _run_archive: captures into the Content Vault,
//                 no destination, copy_style forced to clone.
//   * replay   -> ReplayJobReq / _run_replay: source is a vault archive, posts to
//                 destinations with a fixed per-message delay (0-120s).
export const JOB_MODES = [
  {
    value: "live",
    label: "Live",
    description: "Copy new messages to the destination as they arrive in the source channel.",
  },
  {
    value: "historical",
    label: "Historical",
    description: "Copy an existing range of past messages from the source to the destination, then finish.",
  },
  {
    value: "mass_replace",
    label: "Mass Replace",
    description: "Rewrite text in messages already posted in a channel you administer. No source channel is involved.",
  },
  {
    value: "archive",
    label: "Archive",
    description: "Capture a source channel's history into the local Content Vault. Nothing is posted anywhere.",
  },
  {
    value: "replay",
    label: "Replay",
    description: "Rebuild a destination channel in order from a vault archive, one message at a time with a fixed delay. The original source is not needed.",
  },
];

export const MODE_VALUES = JOB_MODES.map((m) => m.value);

// Modes the shared JobFormDialog owns. Mass replace has its own dialog; the
// selector routes to it rather than duplicating that form here.
export const DIALOG_MODES = ["live", "historical", "archive", "replay"];

export const emptyForm = {
  name: "",
  mode: "live",
  // live / historical (two-account model)
  source_account_id: "",
  dest_account_id: "",
  // archive / replay (single-account model, as ArchiveJobReq/ReplayJobReq define it)
  account_id: "",
  copy_style: "branded",
  source: "",
  destinations: [],
  forward_channel: "",
  rule_ids: [],
  template_ids: [],
  destination_template_ids: {},
  min_id: "",
  max_id: "",
  from_date: "",
  to_date: "",
  // replay only
  vault_channel_id: "",
  replay_delay: "2",
  include_deleted: false,
};

// Saved ISO instant -> value for a <input type="datetime-local"> in the operator's
// own timezone (what they see is what the job will use, to the minute).
const pad2 = (n) => String(n).padStart(2, "0");
export const dateInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const isoOrNull = (d) => (d ? new Date(d).toISOString() : null);
const intOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number.parseInt(v, 10));
const listOrNull = (list) => (list && list.length ? list : null);

// Saved job -> form state. Never converts the mode: an archive job opens as an
// archive job. Unknown modes are passed through so the dialog can refuse them.
export const formFromJob = (job) => ({
  ...emptyForm,
  ...job,
  mode: job.mode || "live",
  source_account_id: job.source_account_id || job.account_id || "",
  dest_account_id: job.dest_account_id || job.account_id || "",
  account_id: job.account_id || job.source_account_id || "",
  copy_style: job.copy_style || "branded",
  source: job.source || "",
  destinations: job.destinations || [],
  forward_channel: job.forward_channel || "",
  rule_ids: job.rule_ids || [],
  template_ids: job.template_ids || [],
  destination_template_ids: job.destination_template_ids || {},
  min_id: job.min_id != null ? String(job.min_id) : "",
  max_id: job.max_id != null ? String(job.max_id) : "",
  from_date: dateInput(job.from_date),
  to_date: dateInput(job.to_date),
  vault_channel_id: job.vault_channel_id != null ? String(job.vault_channel_id) : "",
  replay_delay: job.replay_delay != null ? String(job.replay_delay) : "2",
  include_deleted: Boolean(job.include_deleted),
});

// Which fields the backend's JobUpdateReq lets us change for an existing job.
// Anything not listed is shown read-only in the editor rather than silently dropped.
//
// "mode" is locked for EVERY edit: the job-type selector must never let a Live job
// be re-dressed as an Archive/Replay job, because JobUpdateReq cannot change a job
// into (or out of) those modes and the save would silently keep the old one. The
// only supported switch — Live <-> Historical — stays on the inner Mode dropdown,
// which is what the backend actually accepts.
export const lockedOnEdit = (mode) => {
  if (mode === "archive") return ["mode", "account_id"];
  if (mode === "replay") return ["mode", "account_id", "vault_channel_id"];
  return ["mode"];
};

const rangeError = (form, withMax) => {
  const min = intOrNull(form.min_id);
  const max = withMax ? intOrNull(form.max_id) : null;
  if (min !== null && (Number.isNaN(min) || min < 0)) return "After message ID must be zero or a positive number.";
  if (max !== null && (Number.isNaN(max) || max < 0)) return "Up to message ID must be zero or a positive number.";
  if (min && max && max < min) return "Up to message ID is lower than After message ID — the range is empty.";
  if (form.from_date && form.to_date && new Date(form.to_date) < new Date(form.from_date)) {
    return "From date is after To date — the range is empty.";
  }
  if (withMax && form.replay_delay !== "" && form.replay_delay !== null) {
    const d = Number.parseFloat(form.replay_delay);
    if (Number.isNaN(d) || d < 0 || d > 120) return "Replay delay must be between 0 and 120 seconds.";
  }
  return null;
};

// Returns an operator-facing sentence, or null when the form can be submitted.
export const validateForm = (form, { editing = false } = {}) => {
  if (!MODE_VALUES.includes(form.mode)) return `Unsupported job type "${form.mode}".`;
  if (!form.name?.trim()) return "Give the job a name.";
  switch (form.mode) {
    case "live":
    case "historical":
      if (!form.source || !form.destinations.length) return "Name, source and a destination are required";
      if (!form.source_account_id || !form.dest_account_id) return "Select source and destination accounts";
      return form.mode === "historical" ? rangeError(form, false) : null;
    case "archive":
      if (!form.account_id) return "Pick the account that is a member of the source channel.";
      if (!form.source?.trim()) return "Pick or type the source channel to archive.";
      return rangeError(form, false);
    case "replay":
      if (!form.account_id) return "Pick the account that will post to the destination.";
      if (!editing && !form.vault_channel_id) return "Pick the vault archive to replay from.";
      if (!form.destinations.length) return "Select at least one destination channel.";
      return rangeError(form, true);
    case "mass_replace":
      return "Mass Replace jobs use their own editor.";
    default:
      return `Unsupported job type "${form.mode}".`;
  }
};

const livePayload = (form) => ({
  name: form.name,
  source_account_id: form.source_account_id,
  dest_account_id: form.dest_account_id,
  mode: form.mode,
  copy_style: form.copy_style,
  source: form.source,
  destinations: form.destinations,
  forward_channel: form.forward_channel?.trim() || null,
  rule_ids: listOrNull(form.rule_ids),
  template_ids: listOrNull(form.template_ids),
  destination_template_ids: Object.fromEntries(
    Object.entries(form.destination_template_ids || {}).filter(
      ([destination, templateId]) => form.destinations.includes(destination) && templateId)),
  min_id: intOrNull(form.min_id),
  from_date: isoOrNull(form.from_date),
  to_date: isoOrNull(form.to_date),
  ...(form.archive_enabled !== undefined ? { archive_enabled: form.archive_enabled } : {}),
  ...(form.on_edit ? { on_edit: form.on_edit } : {}),
  ...(form.on_delete ? { on_delete: form.on_delete } : {}),
});

// Form -> the request to send. Returns {method, path, body}. Callers should
// validateForm() first; this does not re-check.
export const buildRequest = (form, editingId = null) => {
  const editing = Boolean(editingId);
  switch (form.mode) {
    case "live":
    case "historical":
      return editing
        ? { method: "put", path: `/jobs/${editingId}`, body: livePayload(form) }
        : { method: "post", path: "/jobs", body: livePayload(form) };

    case "archive": {
      const range = {
        min_id: intOrNull(form.min_id),
        from_date: isoOrNull(form.from_date),
        to_date: isoOrNull(form.to_date),
      };
      return editing
        // JobUpdateReq: name/source/range are editable; account_id is not.
        ? { method: "put", path: `/jobs/${editingId}`, body: { name: form.name, source: form.source, ...range } }
        // ArchiveJobReq, field for field.
        : { method: "post", path: "/jobs/archive", body: { name: form.name, account_id: form.account_id, source: form.source, ...range } };
    }

    case "replay": {
      const shared = {
        name: form.name,
        destinations: form.destinations,
        copy_style: form.copy_style,
        rule_ids: listOrNull(form.rule_ids),
        template_ids: listOrNull(form.template_ids),
        min_id: intOrNull(form.min_id),
        max_id: intOrNull(form.max_id),
        from_date: isoOrNull(form.from_date),
        to_date: isoOrNull(form.to_date),
        replay_delay: form.replay_delay === "" ? null : Number.parseFloat(form.replay_delay),
        include_deleted: Boolean(form.include_deleted),
      };
      return editing
        // JobUpdateReq accepts every field above; vault_channel_id and account_id are fixed.
        ? { method: "put", path: `/jobs/${editingId}`, body: shared }
        // ReplayJobReq, field for field.
        : { method: "post", path: "/jobs/replay",
            body: { account_id: form.account_id, vault_channel_id: Number.parseInt(form.vault_channel_id, 10), ...shared } };
    }

    default:
      throw new Error(`Unsupported job type "${form.mode}"`);
  }
};

// Per-mode actions the backend actually implements. There is no duplicate
// endpoint for jobs, so none is offered.
export const jobActions = (job) => {
  const running = job.status === "running";
  const active = ["running", "paused", "queued", "rate_limited"].includes(job.status);
  return {
    start: !active,
    pause: ["running", "queued", "rate_limited"].includes(job.status),
    resume: job.status === "paused",
    stop: active,
    validate: true,
    edit: !running,
    logs: true,
    delete: true,
    duplicate: false,
  };
};
